import PanelError from '@wandb/weave/common/components/elements/PanelError';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {JSONNaN} from '@wandb/weave/core';
import {cloneDeep, debounce, isArray} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useLayoutEffect, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Card} from 'semantic-ui-react';

import {getLastHistoryRowWithKey, labelComponent} from '../util/media';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {MediaCardProps} from './MediaCard';
import MessageMediaNotFound from './MessageMediaNotFound';
import {SingleRunLink} from './MultiRunWorkspace/SingleRunMode';
import {useStablePanelId} from './Panel/PanelIDContext';
import {useGetTrackingCallbacks} from './PanelMediaBrowser/MediaBrowserPanelTileTrackingContext';

// Plotly straight up fails to render when the height is set to a value that is too small
// See https://wandb.atlassian.net/browse/WB-7402
const PLOTLY_MIN_HEIGHT = 300;
// It doesn't crash when the width is too small, but it's literally unreadable so...
const PLOTLY_MIN_WIDTH = 300;

const PlotlyCard: React.FunctionComponent<MediaCardProps> = React.memo(
  props => {
    const {
      run,
      mediaKey,
      mediaIndex,
      runSignature,
      width,
      height,
      tileMedia,
      loading,
      customRunName,
      tileMediaSpec,
      getMediaTracking,
    } = props;

    // Div to stick plotly chart into
    const plotlyDivRef = React.useRef<HTMLDivElement>(null);
    const [plotlyRenderError, setPlotlyRenderError] = useState(false);
    const {trackSuccess, trackError} = useGetTrackingCallbacks(
      tileMedia,
      tileMediaSpec,
      getMediaTracking
    );

    const {plotlyData, step, error} = usePlotlyCardData({...props, tileMedia});

    const plotHasPotentialToBlockMainThread = React.useMemo(() => {
      // Candlestick plots with a large number of data points can cause the browser to become unresponsive
      // This was reported by a user here https://weightsandbiases.slack.com/archives/C010Y174QGH/p1708636046834159
      // And confirmed by other plotly users here https://community.plotly.com/t/candlestick-plot-performance/28851/2
      for (const d of plotlyData?.data ?? []) {
        if (d?.type === 'candlestick' && d?.x?.length > 2000) {
          return true;
        }
        // Similarly, contour plots with a large number of size > 50 x 50 can cause the browser to become unresponsive.
        if (
          d?.type === 'contour' &&
          (d?.z?.length > 50 || d?.z?.[0]?.length > 50)
        ) {
          return true;
        }
      }
      return false;
    }, [plotlyData]);

    const [overflowWarningAcknowleged, setOverflowWarningAcknowleged] =
      useState<boolean>(false);

    const shouldShowOverflowWarning =
      plotHasPotentialToBlockMainThread && !overflowWarningAcknowleged;

    // Load plotly library
    type PlotlyType = typeof import('plotly.js');
    const [Plotly, setPlotly] = useState<PlotlyType | null>(null);
    useEffect(() => {
      import('plotly.js').then(setPlotly);
    }, []);

    const plotlyNewPlot = useCallback(
      (...args: Parameters<PlotlyType['react']>): void => {
        if (Plotly == null) {
          return;
        }
        let err = false;

        try {
          // Changed from Plotly.newPlot to Plotly.react to fix disappearing plots due to webgl context limit
          // see https://github.com/plotly/plotly.js/issues/2333#issuecomment-391835536
          Plotly.react(...args);
          trackSuccess();
        } catch (e) {
          err = true;
          trackError();
        }
        setPlotlyRenderError(err);
      },
      [Plotly, trackSuccess, trackError]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const plotlyNewPlotDebounced = useCallback(debounce(plotlyNewPlot, 100), [
      plotlyNewPlot,
    ]);

    // Use the plotly library to render the loaded plotly data to the div
    useEffect(() => {
      // Prevent ugly loading states
      if (
        width === 0 ||
        Plotly == null ||
        plotlyDivRef.current == null ||
        plotlyData == null
      ) {
        return;
      }

      const copy = cloneDeep(plotlyData);
      if (copy.layout) {
        copy.layout.width = Math.max(PLOTLY_MIN_WIDTH, width);
        copy.layout.height = Math.max(PLOTLY_MIN_HEIGHT, height + 14);
        copy.layout.autosize = true;
        // TODO: probably need better logic here...
        if (copy.layout.margin) {
          copy.layout.margin.t = 0;
          copy.layout.margin.l = 0;
          copy.layout.margin.r = 0;
          copy.layout.margin.b = 0;
          copy.layout.pad = 0;
        }
      }

      // Webgl is way faster when these are larger. Unfortunately
      // we also run out of GL contexts this way.
      if (isArray(copy.data)) {
        copy.data = copy.data.map((d: any) => ({
          ...d,
          type: d.type === 'scatter' ? 'scattergl' : d.type,
        }));
      }

      // TODO: get datarevision to work with Plotly.react
      // copy.layout.datarevision = this.state.currentStepIndex;

      if (!shouldShowOverflowWarning) {
        plotlyNewPlotDebounced(plotlyDivRef.current, copy.data, copy.layout, {
          responsive: true,
        });
      }
    }, [
      plotlyData,
      Plotly,
      plotlyNewPlotDebounced,
      width,
      height,
      shouldShowOverflowWarning,
    ]);

    const titleLink = (
      <SingleRunLink
        {...runSignature}
        linkText={customRunName ?? run.displayName}
      />
    );
    const stablePanelId = useStablePanelId();

    return (
      <Card className="plotly-card" style={{width: '100%'}}>
        {/* TITLE (Run Name or Description) */}
        {labelComponent(props, step, titleLink)}
        {shouldShowOverflowWarning ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
            <PanelError
              panelID={stablePanelId}
              mediaType="plotly"
              panelViewType="Media Browser"
              message="This plot has a large number of data points and may be slow to render. It may also cause your browser to become unresponsive."
            />
            <Button
              variant="primary"
              onClick={() => {
                setOverflowWarningAcknowleged(true);
              }}
              style={{margin: 10}}>
              Show Plot
            </Button>
          </div>
        ) : (
          <>
            <div ref={plotlyDivRef} data-test="plotly-plot" />
            {plotlyRenderError ? (
              <PanelError
                panelID={stablePanelId}
                panelViewType="Media Browser"
                mediaType="plotly"
                message={
                  <div>Plotly rendering error, try resizing this panel.</div>
                }
                logErrorMessage="Plotly rendering error, try resizing this panel."
              />
            ) : (
              plotlyData == null &&
              (error != null ? (
                <PanelError
                  panelID={stablePanelId}
                  panelViewType="Media Browser"
                  mediaType="plotly"
                  className="media-missing"
                  message={<div>{error}</div>}
                  logErrorMessage={error}
                />
              ) : loading ? (
                <Loader name="panel-content-loader" size="small" />
              ) : (
                <MessageMediaNotFound
                  mediaIndex={mediaIndex}
                  mediaKey={mediaKey}
                  stepIndex={step}
                  mediaType="table"
                />
              ))
            )}
          </>
        )}
      </Card>
    );
  }
);

export default PlotlyCard;

type PlotlyCardData = {
  plotlyData: any;
  step: number | undefined;
  error: string | null;
};

function usePlotlyCardData(props: MediaCardProps): PlotlyCardData {
  const {run, globalStep, tileMedia, mediaKey} = props;
  const blob = tileMedia?.blob;

  // If this is file-based media, we put the contents of the file here once
  // loaded
  const [filePlotlyData, setFilePlotlyData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (blob == null) {
      return;
    }
    (async () => {
      const fileContents = await new Response(blob).text();
      const parsedPlot = JSONNaN.JSONparseUserFile(fileContents);
      if (parsedPlot.error) {
        setError("Couldn't parse JSON data.");
      } else {
        setFilePlotlyData(parsedPlot.result);
      }
    })();
  }, [blob]);

  const historyRow = getLastHistoryRowWithKey(run, mediaKey, globalStep);

  return {
    plotlyData: filePlotlyData ?? historyRow?.[mediaKey]?.plot ?? null,
    step: tileMedia?.step ?? historyRow?._step,
    error,
  };
}
